import type { PropertyFormValues } from './schema';
import type { PropertyMedia, PropertyNestedRequest } from '@services/housien/types-property';
import { mapFormPricingTaxes } from '@/shared/utils/property-nested-write';
import { buildRentalPricing as buildRentalPricingShared } from '@/shared/utils/rental-nested-write';

/**
 * Computes the rounded price per square meter, returning 0 when inputs are missing.
 */
function pricePerM2(salePrice: number | undefined, constructedArea: number | undefined): number {
  if (!salePrice || !constructedArea || constructedArea === 0) return 0;
  return Math.round(salePrice / constructedArea);
}

/**
 * Coerces a falsy value to undefined, preserving the original when truthy.
 */
function orUndef<T>(value: T): T | undefined {
  return value || undefined;
}

/**
 * Builds the sale pricing block if applicable.
 */
function buildSalePricing(
  values: PropertyFormValues,
): NonNullable<PropertyNestedRequest['pricing']>['sale'] | undefined {
  if (!values.transaction_type?.includes('Venda') || !values.sale_price) return undefined;
  return {
    price: values.sale_price,
    price_per_sqm: pricePerM2(values.sale_price, values.constructed_area) || undefined,
    accepts_exchange: values.accepts_exchange === true ? true : undefined,
  };
}

/**
 * Builds the rental pricing block if applicable.
 */
function buildRentalPricing(
  values: PropertyFormValues,
): NonNullable<PropertyNestedRequest['pricing']>['rental'] | undefined {
  return buildRentalPricingShared({
    transaction_type: values.transaction_type,
    rental_price: values.rental_price,
    rental_payment_frequency: values.rental_payment_frequency,
    rental_guarantee_type: values.rental_guarantee?.length
      ? values.rental_guarantee[0]
      : undefined,
    rental_included_expenses: values.rental_included_expenses,
    rental_min_contract_months: orUndef(values.lease_term_in_months),
    rental_administration: orUndef(values.administration_type),
    constructed_area: values.constructed_area,
  }) as NonNullable<PropertyNestedRequest['pricing']>['rental'] | undefined;
}

/**
 * Assembles a pricing object from optional sale/rental/taxes parts.
 *
 * @param sale - Optional sale block.
 * @param rental - Optional rental block.
 * @param taxes - Optional taxes block.
 * @returns Pricing payload, or undefined when all parts are absent.
 */
function assemblePricing(
  sale: ReturnType<typeof buildSalePricing>,
  rental: ReturnType<typeof buildRentalPricing>,
  taxes: ReturnType<typeof mapFormPricingTaxes>,
): PropertyNestedRequest['pricing'] | undefined {
  const entries = [
    ['sale', sale],
    ['rental', rental],
    ['taxes', taxes],
  ].filter((entry) => entry[1] != null);
  if (entries.length === 0) return undefined;
  return Object.fromEntries(entries) as PropertyNestedRequest['pricing'];
}

/**
 * Builds the pricing block from form values, including taxes.
 *
 * @param values - Form values.
 * @returns Pricing payload, or undefined when empty.
 */
export function buildPricing(values: PropertyFormValues): PropertyNestedRequest['pricing'] | undefined {
  return assemblePricing(
    buildSalePricing(values),
    buildRentalPricing(values),
    mapFormPricingTaxes({ iptu: values.iptu }),
  );
}

/**
 * Builds the condominium section from form values.
 */
export function buildCondominium(values: PropertyFormValues) {
  if (!values.condominium) return undefined;
  return {
    monthly_fee: orUndef(values.condominium_price),
    leisure_areas: values.condo_amenities?.length ? values.condo_amenities : undefined,
    security: values.condo_security?.length ? values.condo_security : undefined,
    included_expenses: values.condo_included_expenses?.length
      ? values.condo_included_expenses
      : undefined,
    paid_by: orUndef(values.condo_paid_by),
  };
}

/**
 * Builds the documentation block when any registry field is present.
 *
 * @param values - Form values.
 * @returns Documentation payload or undefined.
 */
function buildDocumentation(values: PropertyFormValues) {
  if (!values.registration_number && !values.registry_office && !values.inscription_number) {
    return undefined;
  }
  return {
    registration_number: orUndef(values.registration_number),
    registry_office: orUndef(values.registry_office),
    inscription_number: orUndef(values.inscription_number),
  };
}

/**
 * Builds the ownership / mandate block when owner or mandate is present.
 *
 * @param values - Form values.
 * @returns Ownership payload or undefined.
 */
function buildOwnership(values: PropertyFormValues) {
  if (!values.owner_client_id && !values.mandate_type) return undefined;
  return {
    owner_client_id: orUndef(values.owner_client_id),
    mandate: values.mandate_type
      ? {
          mandate_type: values.mandate_type,
          start_date: orUndef(values.mandate_start_date),
          end_date: orUndef(values.mandate_end_date),
          commission_percentage: orUndef(values.mandate_commission),
        }
      : undefined,
  };
}

/**
 * Builds the assignment block from the sourcing member and broker selections.
 *
 * @param values - Form values.
 * @returns Assignment payload or undefined.
 */
function buildAssignment(values: PropertyFormValues) {
  const assignedBrokers = values.assigned_brokers?.length ? values.assigned_brokers : undefined;
  if (!values.captured_by && !values.primary_broker && !assignedBrokers) return undefined;
  return {
    captured_by: orUndef(values.captured_by),
    primary_broker: orUndef(values.primary_broker),
    assigned_brokers: assignedBrokers,
  };
}

/**
 * Builds the media block: the tour link when set, and the video links.
 *
 * The video links are sent whenever the form carries them, empty list
 * included, because removing the last link has to reach the server; the
 * backend merges `media` key by key, so the photos are left alone.
 */
function buildMedia(values: PropertyFormValues): PropertyMedia | undefined {
  const media: PropertyMedia = {};
  if (values.virtual_tour_url) media.virtual_tour_url = values.virtual_tour_url;
  if (Array.isArray(values.video_links)) media.videos = values.video_links;
  return Object.keys(media).length > 0 ? media : undefined;
}

/**
 * Builds documentation / media / ownership blocks when present.
 *
 * @param values - Form values.
 * @param request - Nested request being populated.
 */
export function buildOptionalSections(
  values: PropertyFormValues,
  request: PropertyNestedRequest,
): void {
  const documentation = buildDocumentation(values);
  if (documentation) request.documentation = documentation;
  const media = buildMedia(values);
  if (media) request.media = media;
  const ownership = buildOwnership(values);
  if (ownership) request.ownership = ownership;
  const assignment = buildAssignment(values);
  if (assignment) request.assignment = assignment;
}

/**
 * Builds the agency code block, only when the broker changed it.
 *
 * An unchanged code is left out on purpose: the form opens pre-filled with the
 * code a spreadsheet import stored, and re-sending it would silently turn that
 * into an `agency_code` on every unrelated save. A cleared field is sent as an
 * empty `agency_code`, which is the explicit "no code" the backend reads.
 *
 * @param values - Form values.
 * @returns The `external_ids` block, or undefined when the code did not change.
 */
export function buildExternalIds(values: PropertyFormValues): { agency_code: string } | undefined {
  const code = (values.property_code ?? '').trim();
  const initial = (values.property_code_initial ?? '').trim();
  return code === initial ? undefined : { agency_code: code };
}

/**
 * Adds the agency code block to the request when the broker changed the code.
 *
 * @param values - Form values.
 * @param request - Nested request being populated.
 */
export function applyExternalIds(values: PropertyFormValues, request: PropertyNestedRequest): void {
  const externalIds = buildExternalIds(values);
  if (externalIds) request.external_ids = externalIds;
}

/**
 * Builds the description and metadata sections.
 */
export function buildDescriptionAndMetadata(
  values: PropertyFormValues,
  request: PropertyNestedRequest,
): void {
  request.description = {
    headline: orUndef(values.property_title),
    full_text: orUndef(values.description),
    highlights: values.highlights?.length ? values.highlights : undefined,
  };
  if (values.tags?.length) {
    request.metadata = { tags: values.tags };
  }
}
